import { NavigationExtras, Params, UrlMatchResult, UrlSegment } from '@angular/router';
import { Booking, UtmCampaign, UtmMedium, UtmSource } from '@luggagehero/shared/interfaces';

/**
 * Router commands for the storage-search page — where a traveler goes when the storage location
 * they asked for cannot be shown to them (deactivated, or no longer there).
 *
 * The route differs per platform, so this must not be hardcoded at call sites: web-traveler serves
 * search at `home`, while ionic-traveler has no `home` route at all (its `''` redirects to
 * `/find-shops`, and there is no wildcard, so `/home` would simply fail to resolve).
 *
 * Web targets `/home` rather than the equivalent `/`: going through the `'' -> home` redirect drops
 * `replaceUrl`, so a redirect meant to replace a dead page pushes a history entry instead and leaves
 * the Back button bouncing the traveler straight back into it.
 */
export const searchPageRoute = (isNative: boolean): string[] => [isNative ? '/' : '/home'];

/** The booking id in a booking page path, `/bookings/:id` or `/bookings/:id/dropoff`; null on any other path. */
export const bookingIdFromPath = (pathname: string): string | null => {
  const match = /^\/bookings\/([^/;]+)(?:\/dropoff)?\/?$/.exec(pathname);
  if (!match || match[1] === 'user') {
    return null;
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A malformed escape in a pasted link: not a booking page we can prefetch
    return null;
  }
};

/**
 * Whether a path opens a page with a map, so start-up can begin loading the Maps SDK before the route asks for
 * it: `/` (redirects to home), `home`, `home/find-shop`, `shop-details/:id`, `places/:id` and `demo`, with or without
 * matrix params (`/home;location=…` is how the landing pages send a search).
 */
export const isMapPagePath = (pathname: string): boolean =>
  /^\/(?:home(?:;[^/]*)?(?:\/find-shop(?:;[^/]*)?|\/find-storage)?|shop-details\/[^/]+|places\/[^/]+|demo(?:\/.*)?)?\/?$/.test(
    pathname,
  );

/** Matches, without consuming a segment, only URLs under /bookings, so the bookings module's own routes match them. */
export function bookingsUrlMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  return segments[0]?.path === 'bookings' ? { consumed: [] } : null;
}

export const bookingNavigationExtras = (
  bookingOrToken: string | Booking,
  utmSource: UtmSource,
  utmMedium?: UtmMedium,
  utmCampaign?: UtmCampaign,
): NavigationExtras => {
  const token = typeof bookingOrToken === 'string' ? bookingOrToken : bookingOrToken?.bookingToken;
  return {
    queryParams: new ParamsBuilder()
      .withBookingToken(token)
      .withUtmSource(utmSource)
      .withUtmMedium(utmMedium)
      .withUtmCampaign(utmCampaign)
      .create(),
  };
};

export class ParamsBuilder {
  private params: Params = {};

  public withBookingToken(token: string | undefined): ParamsBuilder {
    if (token) {
      this.params['t'] = token;
    }
    return this;
  }

  public withUtmSource(source: UtmSource | undefined): ParamsBuilder {
    if (source) {
      this.params['utm_source'] = source;
    }
    return this;
  }

  public withUtmMedium(medium: UtmMedium | undefined): ParamsBuilder {
    if (medium) {
      this.params['utm_medium'] = medium;
    }
    return this;
  }

  public withUtmCampaign(campaign: UtmCampaign | undefined): ParamsBuilder {
    if (campaign) {
      this.params['utm_campaign'] = campaign;
    }
    return this;
  }

  public create(): Params {
    return this.params;
  }
}
